import {
    createHealthStat,
    getTikTokHealthStats,
    getTikTokRelatedHealthStats,
} from 'state/utils';
import { endpointWrapper } from './meta.api';
import { isNew } from 'utils/posts';
import { tooltips } from '@/copy';

export const getTikTokData = async (config) =>
    endpointWrapper({
        domain: 'tiktok/data_points',
        ...config,
    }).then(({ data, ...rest }) => ({
        data: data.map(({ data, ...rest }) => ({
            ...rest,
            ...data,
            sentimentScoreInt: data?.sentiment_score,
        })),
        ...rest,
    }));

export const getTikTokWatchlistHashtagData = async (config) =>
    endpointWrapper({
        domain: 'tiktok/watchlist/data_points/hashtags',
        ...config,
    }).then(({ data, ...rest }) => ({
        data: data.map(({ data, ...rest }) => ({
            ...rest,
            ...data,
            sentimentScoreInt: data?.sentiment_score,
        })),
        ...rest,
    }));

export const getTikTokBrands = async (config) =>
    endpointWrapper({
        domain: 'tiktok/brands',
        ...config,
    });

export const getTikTokHashtagsAnyContext = async (config) =>
    endpointWrapper({
        domain: 'tiktok/data_points/hashtags_any_context',
        ...config,
    }).then(({ data, ...rest }) => ({
        data: data.map(({ data, ...rest }) => ({
            ...rest,
            ...data,
            sentimentScoreInt: data?.sentiment_score,
        })),
        ...rest,
    }));

export const getRelatedHashtagData = async (config) =>
    endpointWrapper({
        domain: 'tiktok/data_points_related_hashtag',
        ...config,
    }).then((resp) => {
        const { data } = resp;
        if (!(data && data.length)) return resp;
        return {
            ...resp,
            data: data.map(
                ({
                    id,
                    hashtag,
                    brand,
                    country,
                    brand_name,
                    hashtag_name,
                    price_segmentation,
                    data,
                }) => {
                    const pricePointHealthStat = createHealthStat(
                        'price point',
                        { price_segmentation },
                    );
                    return {
                        id,
                        hashtagId: hashtag,
                        brandId: brand,
                        countryId: country,
                        name: hashtag_name,
                        brandName: brand_name,
                        healthStats: {
                            'price point': pricePointHealthStat,
                        },
                        ...data,
                        price_segmentation: pricePointHealthStat?.score,
                        // TODO: these are transposed in BigQuery
                        views_WoW: data.increase_WoW,
                        views_WoW_increase: data.WoW,
                        parentName: data.affinity_group.map((s) =>
                            s.toLowerCase(),
                        ),
                        fetching: false,
                        fetchingData: false,
                        relatedName: name,
                    };
                },
            ),
        };
    });

export const getRelatedHashtagDataInPairs = async (config) =>
    endpointWrapper({
        domain: 'tiktok/data_points_related_hashtag_pairs',
        ...config,
    }).then((resp) => {
        const { data } = resp;
        if (!(data && data.length)) return resp;
        return {
            ...resp,
            data: data.map(
                ({
                    id,
                    hashtag,
                    brand,
                    country,
                    brand_name,
                    hashtag_name,
                    category,
                    category_name,
                    price_segmentation,
                    data,
                }) => {
                    const pricePointHealthStat = createHealthStat(
                        'price point',
                        { price_segmentation },
                    );
                    return {
                        id,
                        hashtagId: hashtag,
                        brandId: brand,
                        countryId: country,
                        name: hashtag_name,
                        brandName: brand_name,
                        categoryName: category_name,
                        categoryId: category,
                        healthStats: {
                            'price point': pricePointHealthStat,
                        },
                        ...data,
                        price_segmentation: pricePointHealthStat?.score,
                        // TODO: these are transposed in BigQuery
                        views_WoW: data.increase_WoW,
                        views_WoW_increase: data.WoW,
                        parentName: data.affinity_group.map((s) =>
                            s.toLowerCase(),
                        ),
                        fetching: false,
                        fetchingData: false,
                        relatedName: name,
                    };
                },
            ),
        };
    });

export const getRelatedBrandHashtagData = async (config) =>
    endpointWrapper({
        domain: 'tiktok/related_brand_hashtags',
        ...config,
    });

export const getRelatedCategoryHashtagData = async (config) =>
    endpointWrapper({
        domain: 'tiktok/related_category_hashtags',
        ...config,
    });

export const getHashtags = async (config) =>
    endpointWrapper({
        domain: 'hashtags',
        ...config,
    });

export const getRelatedHashtagGroups = async (config) =>
    endpointWrapper({
        domain: 'tiktok/related_hashtag_groups',
        ...config,
    });

// -----------------------------------------------------------------------------
// Keyword + hashtag post aggregation surfaces (replaces legacy related_hashtag).
//
// The new BE jsonb shape uses lowercase keys (`wow`, `mom`, `qoq`, `yoy`,
// `views`, `avg_weekly_views`). Existing TIKTOK_METADATA_BY_KEY entries refer
// to the legacy `non_cum_*_rolling` shape, so we alias both directions here so
// the table components can keep their metric metadata unchanged.
// -----------------------------------------------------------------------------

const RELATED_AGGREGATE_FE_TO_BE_ORDER_BY = {
    non_cum_avg_weekly_views: 'avg_weekly_views',
    non_cum_WoW_rolling: 'wow',
    non_cum_increase_WoW_rolling: 'increase_wow',
    non_cum_MoM_rolling: 'mom',
    non_cum_increase_MoM_rolling: 'increase_mom',
    non_cum_QoQ_rolling: 'qoq',
    non_cum_increase_QoQ_rolling: 'increase_qoq',
    non_cum_YoY_rolling: 'yoy',
    non_cum_increase_YoY_rolling: 'increase_yoy',
};

const translateRelatedAggregateOrderBy = (data) => {
    if (!data || !data.order_by) return data;
    return {
        ...data,
        order_by:
            RELATED_AGGREGATE_FE_TO_BE_ORDER_BY[data.order_by] || data.order_by,
    };
};

const reshapeRelatedAggregateData = (data = {}) => ({
    views: data.views,
    non_cum_avg_weekly_views: data.avg_weekly_views,
    // WoW: cat5 view uses `_rolling`, TT watchlist uses the non-rolling key.
    // Emit both so the same row works in both surfaces.
    non_cum_WoW: data.wow,
    non_cum_WoW_rolling: data.wow,
    non_cum_increase_WoW: data.increase_wow,
    non_cum_increase_WoW_rolling: data.increase_wow,
    non_cum_MoM_rolling: data.mom,
    non_cum_increase_MoM_rolling: data.increase_mom,
    non_cum_QoQ_rolling: data.qoq,
    non_cum_increase_QoQ_rolling: data.increase_qoq,
    non_cum_YoY_rolling: data.yoy,
    non_cum_increase_YoY_rolling: data.increase_yoy,
    affinity_group: data.affinity_group,
    // BE-precomputed health-stat scores; FE only derives the user-facing label.
    hs_avg_weekly_views: data.hs_avg_weekly_views,
});

const shapeRelatedCategoryRow = ({
    id,
    country,
    category,
    brand,
    related_category,
    year,
    week,
    related_category_name,
    brand_name,
    price_segmentation,
    related_type,
    parent_name,
    data,
}) => {
    const reshapedData = reshapeRelatedAggregateData(data);
    const pricePointHealthStat = brand_name
        ? createHealthStat('price point', { price_segmentation })
        : null;
    return {
        id,
        countryId: country,
        categoryId: category,
        brandId: brand,
        relatedCategoryId: related_category,
        year,
        week,
        name: related_category_name,
        brandName: brand_name,
        ...reshapedData,
        healthStats: {
            ...getTikTokRelatedHealthStats(reshapedData),
            ...(pricePointHealthStat && {
                'price point': pricePointHealthStat,
            }),
        },
        ...(pricePointHealthStat && {
            price_segmentation: pricePointHealthStat.score,
        }),
        ...(related_type && { related_type }),
        ...(parent_name && { parent_name }),
        parentName: (data?.affinity_group || []).map((s) => s.toLowerCase()),
        fetching: false,
        fetchingData: false,
    };
};

const shapeRelatedBrandRow = ({
    id,
    country,
    category,
    related_brand,
    year,
    week,
    related_brand_name,
    price_segmentation,
    data,
}) => {
    const reshapedData = reshapeRelatedAggregateData(data);
    const pricePointHealthStat = createHealthStat('price point', {
        price_segmentation,
    });
    return {
        id,
        countryId: country,
        categoryId: category,
        relatedBrandId: related_brand,
        year,
        week,
        name: related_brand_name,
        brandName: related_brand_name,
        ...reshapedData,
        healthStats: {
            ...getTikTokRelatedHealthStats(reshapedData),
            'price point': pricePointHealthStat,
        },
        price_segmentation: pricePointHealthStat?.score,
        parentName: [],
        fetching: false,
        fetchingData: false,
    };
};

const shapeRelatedCategoryGroupRow = ({
    id,
    country,
    category,
    brand,
    related_category_group,
    year,
    week,
    data,
}) => ({
    id,
    countryId: country,
    categoryId: category,
    brandId: brand,
    relatedCategoryGroup: related_category_group,
    year,
    week,
    name: related_category_group,
    ...reshapeRelatedAggregateData(data),
    parentName: [],
});

const shapeBreakdownHashtagRow = ({
    id,
    country,
    category,
    brand,
    related_category,
    related_brand,
    hashtag,
    hashtag_name,
    year,
    week,
    data,
}) => ({
    id,
    countryId: country,
    categoryId: category,
    brandId: brand,
    relatedCategoryId: related_category,
    relatedBrandId: related_brand,
    hashtagId: hashtag,
    name: hashtag_name,
    year,
    week,
    views: data?.views,
});

const shapeBreakdownKeywordRow = ({
    id,
    country,
    category,
    brand,
    related_category,
    related_brand,
    keyword,
    year,
    week,
    data,
}) => ({
    id,
    countryId: country,
    categoryId: category,
    brandId: brand,
    relatedCategoryId: related_category,
    relatedBrandId: related_brand,
    name: keyword,
    keyword,
    year,
    week,
    views: data?.views,
});

const mapDataResponse = (resp, shaper) => {
    const { data } = resp;
    if (!(data && data.length)) return resp;
    return { ...resp, data: data.map(shaper) };
};

// Aggregate tables --------------------------------------------------------

export const getTikTokRelatedAffinityGroups = async (config) =>
    endpointWrapper({
        domain: 'tiktok/related_affinity_groups',
        ...config,
    });

// Watchlist enrichment: latest aggregate row per saved (parent, related_category) pair.
export const getTikTokRelatedCategoryPairs = async (config) =>
    endpointWrapper({
        domain: 'tiktok/data_points_related_category_pairs',
        ...config,
    }).then((resp) => mapDataResponse(resp, shapeRelatedCategoryRow));

export const getTikTokRelatedCategoryAggregate = async ({ data, ...rest }) =>
    endpointWrapper({
        domain: 'tiktok/related_category',
        data: translateRelatedAggregateOrderBy(data),
        ...rest,
    }).then((resp) => mapDataResponse(resp, shapeRelatedCategoryRow));

export const getTikTokRelatedBrandAggregate = async ({ data, ...rest }) =>
    endpointWrapper({
        domain: 'tiktok/related_brand',
        data: translateRelatedAggregateOrderBy(data),
        ...rest,
    }).then((resp) => mapDataResponse(resp, shapeRelatedBrandRow));

export const getTikTokRelatedCategoryGroupAggregate = async ({
    data,
    ...rest
}) =>
    endpointWrapper({
        domain: 'tiktok/related_category_groups',
        data: translateRelatedAggregateOrderBy(data),
        ...rest,
    }).then((resp) => mapDataResponse(resp, shapeRelatedCategoryGroupRow));

// Drawer breakdown — keywords --------------------------------------------

export const getTikTokRelatedCategoryKeywords = async (config) =>
    endpointWrapper({
        domain: 'tiktok/related_category_keywords',
        ...config,
    }).then((resp) => mapDataResponse(resp, shapeBreakdownKeywordRow));

export const getTikTokRelatedBrandKeywords = async (config) =>
    endpointWrapper({
        domain: 'tiktok/related_brand_keywords',
        ...config,
    }).then((resp) => mapDataResponse(resp, shapeBreakdownKeywordRow));

// Drawer breakdown — hashtags --------------------------------------------

export const getTikTokRelatedCategoryBreakdownHashtags = async (config) =>
    endpointWrapper({
        domain: 'tiktok/related_category_breakdown_hashtags',
        ...config,
    }).then((resp) => mapDataResponse(resp, shapeBreakdownHashtagRow));

export const getTikTokRelatedBrandBreakdownHashtags = async (config) =>
    endpointWrapper({
        domain: 'tiktok/related_brand_breakdown_hashtags',
        ...config,
    }).then((resp) => mapDataResponse(resp, shapeBreakdownHashtagRow));

// Drawer breakdown — top posts -------------------------------------------

export const getTikTokRelatedCategoryTopPosts = async (config) =>
    endpointWrapper({
        domain: 'tiktok/related_category_top_posts',
        ...config,
    }).then(shapePosts);

export const getTikTokRelatedBrandTopPosts = async (config) =>
    endpointWrapper({
        domain: 'tiktok/related_brand_top_posts',
        ...config,
    }).then(shapePosts);

function createPaidTypesMessage({ is_ad, partner_hashtag }) {
    let paidTypesMessage;
    if (is_ad == 1 && partner_hashtag) {
        paidTypesMessage = tooltips.isBrandPartnerAndPaidPartnership;
    } else if (is_ad == 1) {
        paidTypesMessage = tooltips.isOnlyPaidPartnership;
    } else if (partner_hashtag) {
        paidTypesMessage = tooltips.isOnlyBrandPartner;
    }
    return paidTypesMessage;
}

export const shapePosts = (resp) => {
    if (resp?.data?.length == 0) return [];
    return resp.data.map(({ data, ...post }) => {
        const sentimentScoreInt = data.sentiment_score;
        const healthStats = getTikTokHealthStats({
            sentiment_score: data.sentiment_score,
            sentimentScoreInt,
        });
        return {
            ...post,
            ...data,
            non_cum_WoW: data.wow,
            sentimentScoreInt,
            isNew: isNew({
                week: post.week,
                year: post.year,
                create_timestamp: data?.create_timestamp,
            }),
            isPaid: !!(data?.is_ad == 1 || data?.partner_hashtag),
            isShop: data?.is_ad == 2,
            searchField: data?.sentiment_summary + ' ' + post?.brand_name,
            paidTypesMessage: createPaidTypesMessage(data),
            healthStats: { 'sentiment score': healthStats['sentiment score'] },
            brand: post?.brand,
            brandName: post?.brand_name,
        };
    });
};

export const getTopPosts = async (config) =>
    endpointWrapper({
        domain: 'tiktok/top_posts',
        ...config,
    }).then(shapePosts);

export const getTopPostsHashtag = async (config) =>
    endpointWrapper({
        domain: 'tiktok/top_posts_hashtag',
        ...config,
    }).then(shapePosts);

export const getTopPostsRelatedHashtags = async (config) =>
    endpointWrapper({
        domain: 'tiktok/top_posts_related_hashtags',
        ...config,
    }).then(shapePosts);

export const getTopPostsRelatedCategories = async (config) =>
    endpointWrapper({
        domain: 'tiktok/related_category_posts',
        ...config,
    }).then(shapePosts);

export const getTopPostsRelatedBrands = async (config) =>
    endpointWrapper({
        domain: 'tiktok/related_brand_posts',
        ...config,
    }).then(shapePosts);

export const getCreators = async (config) =>
    endpointWrapper({
        domain: 'tiktok/creators',
        ...config,
    });

// Drivers of Growth - Time Series Hashtags and Posts by date
export const getTsCategoryHashtags = async (config) =>
    endpointWrapper({
        domain: 'tiktok/ts_category_hashtags',
        ...config,
    });

export const getTsBrandHashtags = async (config) =>
    endpointWrapper({
        domain: 'tiktok/ts_brand_hashtags',
        ...config,
    });

export const getTsCategoryPosts = async (config) =>
    endpointWrapper({
        domain: 'tiktok/ts_category_posts',
        ...config,
    });

export const getTsBrandPosts = async (config) =>
    endpointWrapper({
        domain: 'tiktok/ts_brand_posts',
        ...config,
    });

export const getTsMonthlyCategoryHashtags = async (config) =>
    endpointWrapper({
        domain: 'tiktok/ts_monthly_category_hashtags',
        ...config,
    });

export const getTsMonthlyBrandHashtags = async (config) =>
    endpointWrapper({
        domain: 'tiktok/ts_monthly_brand_hashtags',
        ...config,
    });

export const getTsMonthlyCategoryPosts = async (config) =>
    endpointWrapper({
        domain: 'tiktok/ts_monthly_category_posts',
        ...config,
    });

export const getTsMonthlyBrandPosts = async (config) =>
    endpointWrapper({
        domain: 'tiktok/ts_monthly_brand_posts',
        ...config,
    });

export const getTikTokPostMeta = async (postId) =>
    endpointWrapper({
        domain: `tiktok/post_meta/${postId}`,
        method: 'GET',
    });
